import RoutePageTitle from "components/RoutePageTitle";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useUserContext } from "context/UserContext";
import getGraphQLEndpoint from "utils/getGraphQLEndpoint";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const GET_ABNORMAL_CRM_LIST = `
  query GetAbnormalCrmList($input: JSON!) {
    GetAbnormalCrmList(input: $input)
  }
`;
const PROCESS_ABNORMAL_CRM_ORDER = `
  mutation ProcessAbnormalCrmOrder($input: JSON!) {
    ProcessAbnormalCrmOrder(input: $input)
  }
`;

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  return {
    start: formatDateInput(start),
    end: formatDateInput(end),
  };
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("zh-TW", { hour12: false });
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "是" : "否";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const REISSUE_INVOICE_ACTION_CODES = new Set([
  "reissue_invoice",
  "issue_invoice",
  "invoice_reissue",
]);
const ECPAY_CAPTURE_RETRY_ACTION_CODE = "retry_ecpay_capture";
const EXTERNAL_REFUND_MODE = "external";

const getActionCode = (action) => String(action?.code || action?.action || "").trim();

const isReissueInvoiceAction = (action) =>
  REISSUE_INVOICE_ACTION_CODES.has(getActionCode(action).toLowerCase());

const isRefundAction = (action) => getActionCode(action).toLowerCase() === "refund";
const isExternalRefundAction = (action) => action?.refundMode === EXTERNAL_REFUND_MODE;

const isECPayCaptureRetryAction = (action) =>
  getActionCode(action).toLowerCase() === ECPAY_CAPTURE_RETRY_ACTION_CODE;

const isTWQRPayment = (payment) => {
  const providerType = String(payment?.providerPaymentType || payment?.provider_payment_type || "")
    .trim()
    .toUpperCase();
  const providerLabel = String(
    payment?.providerPaymentLabel || payment?.provider_payment_label || ""
  )
    .trim()
    .toUpperCase();
  return providerType === "TWQR" || providerType.startsWith("TWQR_") || providerLabel === "TWQR";
};

const rowUsesTWQR = (row) => {
  if (isTWQRPayment(row)) return true;
  if (isTWQRPayment(row?.payment)) return true;
  return Array.isArray(row?.payments) && row.payments.some(isTWQRPayment);
};

const getProcessAction = (row) => {
  const actions = Array.isArray(row?.availableActions) ? row.availableActions : [];

  // A reissue action must take precedence over a legacy refund action on the same row.
  const action =
    actions.find(isReissueInvoiceAction) ||
    actions.find(isECPayCaptureRetryAction) ||
    actions.find(isRefundAction);
  if (isRefundAction(action) && rowUsesTWQR(row)) {
    return { ...action, label: "處理流程", name: "處理流程", refundMode: EXTERNAL_REFUND_MODE };
  }
  return action;
};

const renderCellValue = (value) => {
  const display = value === null || value === undefined || value === "" ? "-" : String(value);
  return (
    <MDBox
      sx={{
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        color: "#000",
      }}
      title={display}
    >
      {display}
    </MDBox>
  );
};

const renderWrappedCellValue = (value) => {
  const display = value === null || value === undefined || value === "" ? "-" : String(value);
  return (
    <MDBox
      sx={{
        maxWidth: "100%",
        whiteSpace: "normal",
        overflowWrap: "anywhere",
        wordBreak: "break-word",
        color: "#000",
      }}
      title={display}
    >
      {display}
    </MDBox>
  );
};

const parseDetailValue = (value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  if (
    (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
    (trimmed.startsWith("[") && trimmed.endsWith("]"))
  ) {
    try {
      return JSON.parse(trimmed);
    } catch {
      return value;
    }
  }
  return value;
};

const renderPrimitiveDetail = (value) => (
  <MDTypography
    variant="button"
    sx={{
      whiteSpace: "pre-wrap",
      overflowWrap: "anywhere",
      wordBreak: "break-word",
      color: "#344767",
    }}
  >
    {formatValue(value)}
  </MDTypography>
);

function JsonDetailView({ value, level = 0 }) {
  const parsedValue = parseDetailValue(value);

  if (
    parsedValue === null ||
    parsedValue === undefined ||
    typeof parsedValue === "string" ||
    typeof parsedValue === "number" ||
    typeof parsedValue === "boolean"
  ) {
    return renderPrimitiveDetail(parsedValue);
  }

  if (Array.isArray(parsedValue)) {
    if (!parsedValue.length) return renderPrimitiveDetail("[]");
    return (
      <Stack spacing={1.5}>
        {parsedValue.map((item, index) => (
          <Card
            key={`${level}-${index}`}
            variant="outlined"
            sx={{ borderRadius: 2, boxShadow: "none" }}
          >
            <MDBox px={2} py={1.5}>
              <MDTypography variant="caption" color="text" fontWeight="medium">
                項目 {index + 1}
              </MDTypography>
              <MDBox mt={1}>
                <JsonDetailView value={item} level={level + 1} />
              </MDBox>
            </MDBox>
          </Card>
        ))}
      </Stack>
    );
  }

  const entries = Object.entries(parsedValue || {});
  if (!entries.length) return renderPrimitiveDetail("{}");

  return (
    <TableContainer sx={{ borderRadius: 2, border: "1px solid #eee" }}>
      <Table size="small">
        <TableBody>
          {entries.map(([key, itemValue]) => (
            <TableRow key={`${level}-${key}`}>
              <TableCell sx={{ width: 180, color: "text.secondary", verticalAlign: "top" }}>
                {key}
              </TableCell>
              <TableCell sx={{ verticalAlign: "top" }}>
                <JsonDetailView value={itemValue} level={level + 1} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

JsonDetailView.propTypes = {
  value: PropTypes.any,
  level: PropTypes.number,
};

const renderColGroup = () => (
  <colgroup>
    <col style={{ width: columnWidths.actions }} />
    <col style={{ width: columnWidths.orderId }} />
    <col style={{ width: columnWidths.status }} />
    <col style={{ width: columnWidths.reason }} />
    <col style={{ width: columnWidths.abnormalAt }} />
    <col style={{ width: columnWidths.note }} />
    <col style={{ width: columnWidths.payments }} />
    <col style={{ width: columnWidths.logs }} />
  </colgroup>
);

const columnWidths = {
  orderId: "180px",
  status: "120px",
  reason: "240px",
  abnormalAt: "180px",
  note: "240px",
  payments: "360px",
  logs: "360px",
  actions: "180px",
};

const headerCellSx = {
  whiteSpace: "nowrap",
  fontWeight: 700,
  fontSize: "0.9rem",
  letterSpacing: "0.02em",
  backgroundColor: "#f3f5f9",
  borderBottom: "1px solid #e0e0e0",
};

const bodyCellSx = {
  whiteSpace: "nowrap",
  fontSize: "0.8rem",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

function AbnormalOrdersPage() {
  const { userData } = useUserContext() || {};
  const backendUrl = useMemo(getGraphQLEndpoint, []);
  const email = userData?.email ? userData.email.trim().toLowerCase() : "";
  const defaultRange = useMemo(getDefaultDateRange, []);
  const initialFilters = useMemo(
    () => ({
      id: "",
      start: defaultRange.start,
      end: defaultRange.end,
      size: 20,
    }),
    [defaultRange.end, defaultRange.start]
  );

  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [processDialog, setProcessDialog] = useState({ open: false, row: null, action: null });
  const [detailDialog, setDetailDialog] = useState({ open: false, title: "", value: null });
  const [comment, setComment] = useState("");
  const [resultDialog, setResultDialog] = useState({ open: false, body: null });

  const fetchGraphQL = useCallback(
    async (query, variables = {}) => {
      const response = await fetch(backendUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ query, variables }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok || json?.errors?.length) {
        throw new Error(
          json?.errors?.[0]?.message || `GraphQL request failed (${response.status})`
        );
      }
      return json?.data || {};
    },
    [backendUrl]
  );

  const fetchAbnormalOrders = useCallback(async () => {
    if (!email) return;

    setLoading(true);
    setError("");

    try {
      const payload = {
        email,
        page,
        size: appliedFilters.size,
        orderBy: "DESC",
      };

      if (appliedFilters.id.trim()) {
        payload.id = appliedFilters.id.trim();
      } else {
        if (appliedFilters.start) payload.start = appliedFilters.start;
        if (appliedFilters.end) payload.end = appliedFilters.end;
      }

      const data = await fetchGraphQL(GET_ABNORMAL_CRM_LIST, { input: payload });
      const result = data?.GetAbnormalCrmList || {};
      if (result?.code !== "0") {
        throw new Error(result?.message || "異常單列表讀取失敗");
      }
      const body = result?.body || {};
      setRows(Array.isArray(body?.data) ? body.data : []);
      setTotal(Number(body?.total) || 0);
    } catch (err) {
      setRows([]);
      setTotal(0);
      setError(err.message || "異常單列表讀取失敗");
    } finally {
      setLoading(false);
    }
  }, [
    appliedFilters.end,
    appliedFilters.id,
    appliedFilters.size,
    appliedFilters.start,
    email,
    fetchGraphQL,
    page,
  ]);

  useEffect(() => {
    fetchAbnormalOrders();
  }, [fetchAbnormalOrders]);

  const handleSearch = useCallback(() => {
    setAppliedFilters(filters);
    setPage(1);
  }, [filters]);

  const handleReset = useCallback(() => {
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setPage(1);
  }, [initialFilters]);

  const handleOpenProcessDialog = useCallback((row, action) => {
    setProcessDialog({ open: true, row, action });
    setComment("");
  }, []);

  const handleOpenDetailDialog = useCallback((title, value) => {
    setDetailDialog({ open: true, title, value });
  }, []);

  const handleCloseProcessDialog = useCallback(() => {
    if (processing) return;
    setProcessDialog({ open: false, row: null, action: null });
    setComment("");
  }, [processing]);

  const handleProcessOrder = useCallback(async () => {
    const row = processDialog.row;
    const orderId = row?.id || "";
    const action = getActionCode(processDialog.action);
    if (!orderId || !email || !action) return;

    setProcessing(true);
    setError("");

    try {
      const payload = {
        orderId,
        action: isExternalRefundAction(processDialog.action) ? "refund_external" : action,
        email,
      };
      if (comment.trim()) {
        payload.comment = comment.trim();
      } else if (isExternalRefundAction(processDialog.action)) {
        payload.comment = "已於歐付寶廠商管理後台確認退款完成";
      }

      const data = await fetchGraphQL(PROCESS_ABNORMAL_CRM_ORDER, { input: payload });
      const result = data?.ProcessAbnormalCrmOrder || {};
      if (result?.code !== "0") {
        throw new Error(result?.message || "異常單處理失敗");
      }

      setProcessDialog({ open: false, row: null, action: null });
      setComment("");
      setResultDialog({ open: true, body: result?.body || null });
      await fetchAbnormalOrders();
    } catch (err) {
      setError(err.message || "異常單處理失敗");
    } finally {
      setProcessing(false);
    }
  }, [comment, email, fetchAbnormalOrders, fetchGraphQL, processDialog.action, processDialog.row]);

  const totalPages = Math.max(1, Math.ceil((total || 0) / appliedFilters.size));
  const currentRow = processDialog.row;
  const currentAction = processDialog.action;
  const currentActionIsInvoiceReissue = isReissueInvoiceAction(currentAction);
  const currentActionIsECPayCaptureRetry = isECPayCaptureRetryAction(currentAction);
  const currentActionIsExternalRefund = isExternalRefundAction(currentAction);
  const currentActionLabel =
    String(currentAction?.label || currentAction?.name || "").trim() ||
    (currentActionIsInvoiceReissue ? "補開發票" : "退款處理");

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  <RoutePageTitle />
                </MDTypography>
              </MDBox>
              <MDBox p={3}>
                {error ? (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                ) : null}

                <Grid container spacing={2} mb={2}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="訂單編號"
                      value={filters.id}
                      onChange={(event) =>
                        setFilters((prev) => ({ ...prev, id: event.target.value }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      type="date"
                      label="開始日期"
                      InputLabelProps={{ shrink: true }}
                      value={filters.start}
                      onChange={(event) =>
                        setFilters((prev) => ({ ...prev, start: event.target.value }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      type="date"
                      label="結束日期"
                      InputLabelProps={{ shrink: true }}
                      value={filters.end}
                      onChange={(event) =>
                        setFilters((prev) => ({ ...prev, end: event.target.value }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      select
                      fullWidth
                      label="每頁筆數"
                      value={filters.size}
                      onChange={(event) =>
                        setFilters((prev) => ({ ...prev, size: Number(event.target.value) }))
                      }
                    >
                      {PAGE_SIZE_OPTIONS.map((size) => (
                        <MenuItem key={size} value={size}>
                          {size}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Stack direction="row" spacing={1} height="100%" alignItems="center">
                      <Button
                        fullWidth
                        variant="contained"
                        color="info"
                        sx={{
                          backgroundColor: "#1a73e8",
                          color: "#fff",
                          "&:hover": {
                            backgroundColor: "#1565c0",
                          },
                        }}
                        onClick={handleSearch}
                      >
                        查詢
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={handleReset}
                        sx={{
                          color: "#344767",
                          borderColor: "#c4cdd5",
                          "&:hover": {
                            borderColor: "#344767",
                            backgroundColor: "#f7f9fc",
                          },
                        }}
                      >
                        重設
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>

                <Stack direction="row" spacing={1} alignItems="center" mb={2} flexWrap="wrap">
                  <Chip label={`總筆數 ${total}`} color="info" variant="outlined" />
                  <Chip label={`第 ${page} / ${totalPages} 頁`} variant="outlined" />
                  <Chip label={`登入帳號 ${email || "-"}`} variant="outlined" />
                </Stack>

                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={8}>
                    <CircularProgress color="info" />
                  </MDBox>
                ) : (
                  <TableContainer
                    sx={{
                      width: "100%",
                      borderRadius: 2,
                      border: "1px solid #eee",
                      overflowX: "auto",
                      "& table": {
                        width: "100%",
                        minWidth: "1860px",
                        tableLayout: "fixed",
                      },
                      "& th, & td": {
                        boxSizing: "border-box",
                      },
                    }}
                  >
                    <Table size="small" sx={{ width: "100%" }}>
                      {renderColGroup()}
                      <TableHead
                        sx={{
                          display: "table-header-group",
                          padding: 0,
                          borderRadius: 0,
                        }}
                      >
                        <TableRow>
                          <TableCell align="center" sx={headerCellSx}>
                            可執行動作
                          </TableCell>
                          <TableCell sx={headerCellSx}>訂單編號</TableCell>
                          <TableCell sx={headerCellSx}>異常狀態</TableCell>
                          <TableCell sx={headerCellSx}>異常原因</TableCell>
                          <TableCell sx={headerCellSx}>異常時間</TableCell>
                          <TableCell sx={headerCellSx}>備註</TableCell>
                          <TableCell sx={headerCellSx}>付款資訊</TableCell>
                          <TableCell sx={headerCellSx}>訂單紀錄</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center">
                              無資料
                            </TableCell>
                          </TableRow>
                        ) : (
                          rows.map((row) => {
                            const processAction = getProcessAction(row);
                            const canProcess = Boolean(processAction?.enabled);
                            const processLabel =
                              String(
                                processAction?.label || processAction?.name || row?.label || ""
                              ).trim() || "待支援";
                            return (
                              <TableRow
                                key={row?.pubId || `${row?.abnormalAt || ""}-${row?.note || ""}`}
                              >
                                <TableCell align="center" sx={bodyCellSx}>
                                  {processAction ? (
                                    <Stack spacing={1} alignItems="center">
                                      <Button
                                        variant={canProcess ? "contained" : "outlined"}
                                        color="info"
                                        sx={{
                                          minWidth: 120,
                                          maxWidth: "100%",
                                          whiteSpace: "normal",
                                          overflowWrap: "anywhere",
                                          color: canProcess ? "#fff" : "#5f6b7a",
                                          borderColor: "#cfd8e3",
                                          backgroundColor: canProcess ? "#1a73e8" : "#fff",
                                          "&:hover": {
                                            backgroundColor: canProcess ? "#1565c0" : "#f7f9fc",
                                            borderColor: "#cfd8e3",
                                          },
                                          "&.Mui-disabled": {
                                            color: "#5f6b7a",
                                            borderColor: "#cfd8e3",
                                            backgroundColor: "#f7f9fc",
                                          },
                                        }}
                                        disabled={!canProcess}
                                        onClick={() => handleOpenProcessDialog(row, processAction)}
                                      >
                                        {processLabel}
                                      </Button>
                                    </Stack>
                                  ) : (
                                    <Chip label="待支援" variant="outlined" />
                                  )}
                                </TableCell>
                                <TableCell sx={bodyCellSx}>
                                  {renderWrappedCellValue(formatValue(row?.id))}
                                </TableCell>
                                <TableCell sx={bodyCellSx}>
                                  <Chip
                                    size="small"
                                    color="error"
                                    label={formatValue(row?.status)}
                                  />
                                </TableCell>
                                <TableCell sx={bodyCellSx}>
                                  <Stack spacing={0.5}>
                                    <MDTypography
                                      variant="button"
                                      fontWeight="medium"
                                      title={formatValue(row?.abnormalReasonText)}
                                      sx={{
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                      }}
                                    >
                                      {formatValue(row?.abnormalReasonText)}
                                    </MDTypography>
                                    <MDTypography variant="caption" color="text">
                                      {formatValue(row?.abnormalReasonCode)}
                                    </MDTypography>
                                  </Stack>
                                </TableCell>
                                <TableCell sx={bodyCellSx}>
                                  {renderCellValue(formatDateTime(row?.abnormalAt))}
                                </TableCell>
                                <TableCell sx={bodyCellSx}>
                                  {renderWrappedCellValue(formatValue(row?.note))}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    ...bodyCellSx,
                                    textAlign: "left",
                                  }}
                                >
                                  <Button
                                    variant="text"
                                    color="info"
                                    size="small"
                                    onClick={() =>
                                      handleOpenDetailDialog("付款資訊", row?.payments)
                                    }
                                  >
                                    查看資料
                                  </Button>
                                </TableCell>
                                <TableCell
                                  sx={{
                                    ...bodyCellSx,
                                    textAlign: "left",
                                  }}
                                >
                                  <Button
                                    variant="text"
                                    color="info"
                                    size="small"
                                    onClick={() => handleOpenDetailDialog("訂單紀錄", row?.logs)}
                                  >
                                    查看資料
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                <MDBox display="flex" justifyContent="center" mt={3}>
                  <Pagination
                    color="error"
                    page={page}
                    count={totalPages}
                    onChange={(_, value) => setPage(value)}
                  />
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog
        open={detailDialog.open}
        onClose={() => setDetailDialog({ open: false, title: "", value: null })}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <MDBox display="flex" alignItems="center" justifyContent="space-between" px={3} pt={3}>
          <MDTypography variant="h6">{detailDialog.title}</MDTypography>
          <IconButton
            onClick={() => setDetailDialog({ open: false, title: "", value: null })}
            size="small"
          >
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
        <DialogContent>
          <JsonDetailView value={detailDialog.value} />
        </DialogContent>
      </Dialog>

      <Dialog open={processDialog.open} onClose={handleCloseProcessDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {currentActionIsInvoiceReissue
            ? "異常訂單補開發票"
            : currentActionIsECPayCaptureRetry
            ? "綠界補發請款"
            : currentActionIsExternalRefund
            ? "TWQR 退款處理流程"
            : "異常訂單退款處理"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Alert severity="warning">
              {currentActionIsInvoiceReissue
                ? "送出後將重新完成已付款、已出貨但發票開立失敗的訂單，並補開發票。"
                : currentActionIsECPayCaptureRetry
                ? "送出後會先向綠界查詢付款狀態；僅已授權付款才會補送請款，並重建當日結帳紀錄。"
                : currentActionIsExternalRefund
                ? "請先自行前往歐付寶廠商管理後台完成此筆退款；確認退款完成後再按「退款」更新訂單狀態。本操作不會呼叫綠界退款。"
                : "送出後將依 CRM 流程確認異常原因、改回 paid，並執行退款。"}
            </Alert>
            <TextField
              fullWidth
              label="訂單編號"
              value={currentRow?.id || ""}
              InputProps={{ readOnly: true }}
            />
            <TextField
              fullWidth
              label="異常原因"
              value={currentRow?.abnormalReasonText || ""}
              InputProps={{ readOnly: true }}
            />
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="處理備註"
              placeholder={
                currentActionIsInvoiceReissue
                  ? "例如：客服確認發票開立失敗，重新補開發票"
                  : currentActionIsECPayCaptureRetry
                  ? "例如：客服確認綠界請款逾時，補送請款"
                  : currentActionIsExternalRefund
                  ? "例如：已於歐付寶後台完成全額退款"
                  : "例如：客服確認為逾時後付款，執行退款"
              }
              value={comment}
              onChange={(event) => setComment(event.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProcessDialog} disabled={processing}>
            取消
          </Button>
          <Button
            variant="contained"
            color={
              currentActionIsInvoiceReissue || currentActionIsECPayCaptureRetry ? "info" : "error"
            }
            sx={{
              color: "#fff",
              backgroundColor:
                currentActionIsInvoiceReissue || currentActionIsECPayCaptureRetry
                  ? "#1a73e8"
                  : "#d32f2f",
              "&:hover": {
                backgroundColor:
                  currentActionIsInvoiceReissue || currentActionIsECPayCaptureRetry
                    ? "#1565c0"
                    : "#b71c1c",
              },
              "&.Mui-disabled": {
                color: "#fff",
                backgroundColor:
                  currentActionIsInvoiceReissue || currentActionIsECPayCaptureRetry
                    ? "#90caf9"
                    : "#ef9a9a",
              },
            }}
            onClick={handleProcessOrder}
            disabled={processing}
          >
            {processing
              ? "處理中..."
              : currentActionIsExternalRefund
              ? "退款"
              : `確認${currentActionLabel}`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={resultDialog.open}
        onClose={() => setResultDialog({ open: false, body: null })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>處理完成</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} mt={1}>
            <Alert severity="success">{resultDialog.body?.message || "異常訂單處理已完成"}</Alert>
            <MDTypography variant="button">
              訂單編號：{formatValue(resultDialog.body?.orderId)}
            </MDTypography>
            <MDTypography variant="button">
              處理前狀態：{formatValue(resultDialog.body?.previousStatus)}
            </MDTypography>
            <MDTypography variant="button">
              處理後狀態：{formatValue(resultDialog.body?.status)}
            </MDTypography>
            <MDTypography variant="button">
              付款方式：{formatValue(resultDialog.body?.paymentMethod)}
            </MDTypography>
            <MDTypography variant="button">
              發票號碼：{formatValue(resultDialog.body?.invoiceNo)}
            </MDTypography>
            <MDTypography variant="button">
              金額：{formatValue(resultDialog.body?.amount)}
            </MDTypography>
            <MDTypography variant="button">
              實付金額：{formatValue(resultDialog.body?.paidAmount)}
            </MDTypography>
            <MDTypography variant="button">
              執行動作：{formatValue(resultDialog.body?.action)}
            </MDTypography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResultDialog({ open: false, body: null })}>關閉</Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default AbnormalOrdersPage;
